import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useHistory, useParams } from 'react-router-dom';
import MUIProvider from '~/contexts/MUIProvider';

import { Container, Stack, Typography } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import Spinner from '~/components/Common/Spinner';
import DealPageForm from '~/components/Cruises/pages/DealsPage/components/DealPageForm';
import { CruiseDealPage, CruiseDealPageCreation } from '~/components/Cruises/pages/DealsPage/types';

import { formatUTCDateDateTimeWidget } from '~/services/TimeService';
import DealCrawlerService, { CruiseDealCrawler, CruiseDealSailingCrawler } from '~/services/cruises/DealCrawlerService';
import DealPageService from '~/services/cruises/DealPageService';
import InclusionsService from '~/services/cruises/InclusionsService';

import DealCrawlerInclusionList, { InclusionForm } from './DealCrawlerInclusionList';

export default function DealCrawlerPageCreation() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [dealCrawler, setDealCrawler] = useState<CruiseDealCrawler | null>(null);
  const [crawlerDepartures, setCrawlerDepartures] = useState<Array<CruiseDealSailingCrawler>>([]);
  const [crawlerPromotions, setCrawlerPromotions] = useState<Array<string>>([]);
  const [isSaving, setIsSaving] = useState(false);

  const [offerInclusions, setOfferInclusions] = useState<Array<InclusionForm>>([]);
  const [dealPageInclusions, setDealPageInclusions] = useState<Array<InclusionForm>>([]);

  const { enqueueSnackbar } = useSnackbar();

  const fetchSailings = useCallback(async () => {
    try {
      const response = await DealCrawlerService.getSailings(id);
      setCrawlerDepartures(response.result);

      const promotions = response.result.flatMap((sailing) => sailing.rates?.flatMap((rate) => rate.promotions) || []);
      const uniquePromotions = [...new Set(promotions)];
      setCrawlerPromotions(uniquePromotions);

      const inclusions = uniquePromotions.map((promotion) => ({
        id: promotion,
        description: promotion,
        isActive: true,
      }));
      setDealPageInclusions(inclusions);
      setOfferInclusions(inclusions);
    } catch (error) {
      enqueueSnackbar('Error fetching sailings', { variant: 'error' });
    }
  }, [id, enqueueSnackbar]);

  const fetchDealCrawler = useCallback(async () => {
    try {
      const response = await DealCrawlerService.getById(id);
      setDealCrawler(response.result);

      fetchSailings();
    } catch (error) {
      enqueueSnackbar('Error fetching deal crawler', { variant: 'error' });
    }
  }, [id, enqueueSnackbar, fetchSailings]);

  const saveInclusions = useCallback(
    async (dealPage: CruiseDealPage) => {
      if (!dealPageInclusions.length) {
        return null;
      }

      let offerInclusionId: string | null = null;

      const activeDealInclusions = dealPageInclusions.filter((inclusion) => inclusion.isActive);
      if (activeDealInclusions.length) {
        try {
          await Promise.all(
            activeDealInclusions.map((inclusion) =>
              DealPageService.createDealInclusion(dealPage.id, {
                description: inclusion.description,
                icon: 'check',
                isHighlighted: false,
              }),
            ),
          );
        } catch (error) {
          console.error(error);
          enqueueSnackbar('Error saving deal page inclusions', { variant: 'error' });
        }
      }

      const activeOfferInclusions = offerInclusions.filter((inclusion) => inclusion.isActive);
      if (activeOfferInclusions.length && crawlerDepartures.length && dealCrawler) {
        try {
          const createdInclusion = await InclusionsService.create({
            name: dealCrawler.dealPageName,
            startDate: dealPage.startDate,
            endDate: dealPage.endDate,
            type: 'STANDARD',
            fkVendorId: dealCrawler.fkVendorId,
            filters: crawlerDepartures.map(({ departure }) => ({
              filterId: departure.id,
              filterType: 'DEPARTURE',
            })),
            items: activeOfferInclusions.map((inclusion) => ({
              type: 'FREE_TEXT',
              description: inclusion.description,
              isHighlighted: false,
            })),
          });

          offerInclusionId = createdInclusion.id;
        } catch (error) {
          enqueueSnackbar('Error saving offer inclusions', { variant: 'error' });
        }
      }

      return offerInclusionId;
    },
    [dealPageInclusions, enqueueSnackbar, dealCrawler, crawlerDepartures, offerInclusions],
  );

  const handleSubmit = useCallback(
    async (formValues: CruiseDealPageCreation) => {
      setIsSaving(true);

      try {
        const createdDealPage = await DealPageService.create({
          ...formValues,
          startDate: formatUTCDateDateTimeWidget(new Date(formValues.startDate)),
          endDate: formatUTCDateDateTimeWidget(new Date(formValues.endDate)),
        });

        const inclusionId = await saveInclusions(createdDealPage);

        enqueueSnackbar('Deal page created successfully', { variant: 'success' });
        history.push(
          `/cruises/deals/${createdDealPage.id}/edit${inclusionId ? `?newOfferInclusionId=${inclusionId}` : ''}`,
        );
      } catch (error) {
        enqueueSnackbar(`Error saving deal page: ${error}`, { variant: 'error' });
      } finally {
        setIsSaving(false);
      }
    },
    [enqueueSnackbar, history, saveInclusions],
  );

  useEffect(() => {
    if (!dealCrawler) {
      fetchDealCrawler();
    }
  }, [dealCrawler, fetchDealCrawler]);

  const isReady = useMemo(() => dealCrawler && crawlerDepartures.length > 0, [dealCrawler, crawlerDepartures]);

  return (
    <MUIProvider>
      <Helmet>
        <title>Create Deal Page From Crawler Data</title>
      </Helmet>

      <Container maxWidth="xl">
        <PageHeader title="Create Deal Page From Crawler Data" backButton />

        {isReady && (
          <Stack direction="column" spacing={4}>
            <Typography variant="h6">
              Crawled Page:
              <a href={dealCrawler.sourceUrl} target="_blank" rel="noopener noreferrer">
                {dealCrawler.sourceUrl}
              </a>
            </Typography>

            <DealPageForm
              dealCrawler={dealCrawler}
              crawlerDepartures={crawlerDepartures}
              onSubmit={handleSubmit}
              isFetching={isSaving}
            >
              {crawlerPromotions.length > 0 && (
                <Stack direction="column" spacing={2} marginTop={4}>
                  <DealCrawlerInclusionList
                    title="Deal Page Inclusions"
                    subtitle="These inclusions will be displayed at the top of the deal page"
                    inclusionList={dealPageInclusions}
                    setInclusionList={setDealPageInclusions}
                    isEditEnabled
                  />

                  <DealCrawlerInclusionList
                    title="Offer Inclusions"
                    subtitle="These inclusions will be displayed in the offer tile"
                    inclusionList={offerInclusions}
                    setInclusionList={setOfferInclusions}
                    isEditEnabled
                  />
                </Stack>
              )}
            </DealPageForm>
          </Stack>
        )}

        {!isReady && (
          <Stack direction="row" justifyContent="center" alignItems="center" height="100%">
            <Spinner />
          </Stack>
        )}
      </Container>
    </MUIProvider>
  );
}
